import { Injectable } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { CarouselType, DisplayPriceAndCurrency, LocalizedData } from '../../../common';
import {
  Carousel,
  Content,
  ContinueWatchingCarousel,
  GenericCarousel,
  GenericCarouselItem,
  HeroItem,
  MarketingEmailBlocker,
  MyListCarousel,
  RecommendationCarousel,
  SeasonCarousel,
  SpotlightTray,
} from '../../../domain';
import { AbstractBookmarkService } from '../../services/bookmarking/abstract-bookmark.service';
import { formatRecommendationLocalizedTitle } from '../../utils';
import { BasicCarouselViewModel } from '../basic-carousel/basic-carousel.view-model';
import { ContentViewModel, ContentViewModelCarouselType } from '../carousel-item/content-view.model';
import { CollectionCarouselViewModel } from '../collection-carousel/collection-carousel.view-model';
import { ContinueWatchingCarouselViewModel } from '../continue-watching-carousel/continue-watching-carousel.view-model';
import { GenericCarouselItemViewModel } from '../generic-carousel-item/generic-carousel-item.view-model';
import { GenericCarouselViewModel } from '../generic-carousel/generic-carousel.view-model';
import { HeroItemViewModel } from '../hero-item/hero-item.view-model';
import { MarketingEmailBlockerViewModel } from '../marketing-email-blocker/marketing-email-blocker.view-model';
import { MyListCarouselViewModel } from '../my-list-carousel/my-list-carousel.view-model';
import { RecommendationCarouselViewModel } from '../recommendation-carousel/recommendation-carousel.view-model';
import { SeasonBasicCarouselViewModel } from '../season-basic-carousel/season-basic-carousel.view-model';
import { SeasonCollectionCarouselViewModel } from '../season-collection-carousel/season-collection-carousel.view-model';
import { SpotlightViewModel } from '../spotlight-tray/spotlight-tray.view-model';
import { TopCarouselViewModel } from '../top-carousel/top-carousel.view-model';

@Injectable({ providedIn: 'root' })
export class PageScreenMapperService {
  constructor(private readonly bookmarkService: AbstractBookmarkService) {}

  // eslint-disable-next-line prefer-arrow/prefer-arrow-functions, complexity
  public carouselItemMapper(content: Content, index: number, carouselType: ContentViewModelCarouselType): ContentViewModel {
    return {
      type: content.type,
      title: content.title,
      guid: content.guid,
      id: content.id,
    };
  }

  // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
  public heroContentPreviewMapper(heroItems: HeroItem[], contents: Content[], currentLang: string): HeroItemViewModel[] {
    if (!contents) {
      return [];
    }

    return heroItems
      .filter((item) => contents.find((it) => it.id === item.contentId))
      .map((heroItem, index) => {
        const contentOfHero = contents.find((it) => it.id === heroItem.contentId);

        return {
          content: this.carouselItemMapper(contentOfHero, index, ContentViewModelCarouselType.Hero),
          backgroundImageUrlsByPlatform: heroItem.backgroundImageUrlsByPlatform,
          ribbon: heroItem.ribbon[currentLang],
        };
      });
  }

  // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
  public carouselTypeMapper(
    carousel: Carousel,
    contentGroupData: Content[][],
    index: number,
    currentLang: string,
    pageURL: string
  ): BasicCarouselViewModel | TopCarouselViewModel | CollectionCarouselViewModel {
    const commonAttributes = {
      items:
        contentGroupData[index]?.map((contentPreview, idx) =>
          this.carouselItemMapper(contentPreview, idx, ContentViewModelCarouselType.Basic)
        ) ?? [],
      titleToDisplay: carousel.titleLocalized[currentLang],
      localizedTitles: carousel.titleLocalized,
      description: carousel.descriptionLocalized ? carousel.descriptionLocalized[currentLang] : undefined,
      layout: carousel.layout,
      slug: carousel.slug,
      routerLink: pageURL,
    };
    switch (carousel.carouselType) {
      case CarouselType.BasicCarousel:
        return {
          ...commonAttributes,
          type: CarouselType.BasicCarousel,
          contentUrl: carousel.contentGroupId,
          resolved: false,
        };
      case CarouselType.TopCarousel:
        return {
          ...commonAttributes,
          type: CarouselType.TopCarousel,
          contentUrl: carousel.contentGroupId,
          resolved: false,
        };
      case CarouselType.CollectionCarousel:
        return {
          ...commonAttributes,
          backgroundImageUrl: carousel.backgroundImageUrl,
          backgroundImageUrlsByPlatform: carousel.backgroundImageUrlsByPlatform,
          titleTreatmentUrl: carousel.titleTreatmentUrl,
          type: CarouselType.CollectionCarousel,
          contentUrl: carousel.contentGroupId,
          resolved: false,
          collectionPageSlug: carousel.collectionPageSlug,
        };
    }
  }

  // eslint-disable-next-line prefer-arrow/prefer-arrow-functions

  // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
  public seasonCarouselTypeMapper(
    carousel: SeasonCarousel,
    contentPreviewData: Content[][],
    index: number,
    currentLang: string,
    pageURL: string
  ): SeasonBasicCarouselViewModel | SeasonCollectionCarouselViewModel {
    return {
      items: contentPreviewData[index]
        ? contentPreviewData[index].map((contentPreview, idx) =>
            this.carouselItemMapper(contentPreview, idx, ContentViewModelCarouselType.Season)
          )
        : [],
      titleToDisplay: carousel.titleLocalized[currentLang],
      localizedTitles: carousel.titleLocalized,
      description: carousel.descriptionLocalized ? carousel.descriptionLocalized[currentLang] : undefined,
      layout: carousel.layout,
      slug: carousel.slug,
      type: carousel.carouselType as CarouselType.SeasonBasicCarousel | CarouselType.SeasonCollectionCarousel,
      backgroundImageUrl: carousel.backgroundImageUrl,
      backgroundImageUrlsByPlatform: carousel.backgroundImageUrlsByPlatform,
      seriesId: carousel.seriesId,
      resolved: false,
      routerLink: pageURL,
    };
  }

  // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
  public continueWatchingCarouselTypeMapper(
    carousel: ContinueWatchingCarousel,
    contentGroupData: Content[][],
    index: number,
    currentLang: string,
    routerLink: string
  ): ContinueWatchingCarouselViewModel {
    return {
      items:
        contentGroupData[index]?.map((contentPreview, idx) =>
          this.carouselItemMapper(contentPreview, idx, ContentViewModelCarouselType.ContinueWatching)
        ) ?? [],
      titleToDisplay: carousel.titleLocalized[currentLang],
      localizedTitles: carousel.titleLocalized,
      slug: carousel.slug,
      type: CarouselType.ContinueWatchingCarousel,
      resolved: false,
      routerLink,
    };
  }

  // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
  public myListCarouselTypeMapper(
    carousel: MyListCarousel,
    contentGroupData: Content[][],
    index: number,
    currentLang: string,
    routerLink: string
  ): MyListCarouselViewModel {
    return {
      items:
        contentGroupData[index]?.map((contentPreview, idx) =>
          this.carouselItemMapper(contentPreview, idx, ContentViewModelCarouselType.MyList)
        ) ?? [],
      titleToDisplay: carousel.titleLocalized[currentLang],
      localizedTitles: carousel.titleLocalized,
      slug: carousel.slug,
      type: CarouselType.MyListCarousel,
      resolved: false,
      routerLink,
    };
  }

  // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
  public genericCarouselMapper(
    carousel: GenericCarousel,
    currentLang: string,
    pageURL: string,
    pageSlug: string
  ): GenericCarouselViewModel {
    return {
      type: CarouselType.GenericCarousel,
      displayedOnTopOfPage: carousel.displayedOnTopOfPage,
      items: carousel.items.map((carouselItem, idx) => this.genericCarouselItemMapper(carousel, carouselItem, idx, currentLang, pageSlug)),
      titleToDisplay: carousel.titleLocalized[currentLang],
      localizedTitles: carousel.titleLocalized,
      slug: carousel.slug,
      layout: carousel.layout,
      resolved: false,
      routerLink: pageURL,
    };
  }

  // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
  public genericCarouselItemMapper(
    carousel: GenericCarousel,
    carouselItem: GenericCarouselItem,
    index: number,
    currentLang: string,
    pageSlug: string
  ): GenericCarouselItemViewModel {
    return {
      titleToDisplay: carouselItem.titleLocalized[currentLang],
      localizedTitles: carouselItem.titleLocalized,
      index: index + 1,
      imageUrl: carouselItem.imageUrl[currentLang],
      backgroundImageUrlsByPlatform: carouselItem.backgroundImageUrlsByPlatform.map((backgroundImage) => ({
        platform: backgroundImage.platform,
        imageUrl: backgroundImage.imageUrl[currentLang],
      })),
      feedUrl: carouselItem.feedUrl,
      pageSlug: carouselItem.pageSlug,
      genre: carouselItem.genre,
      titleSlug: carouselItem.titleSlug,
      pageItemSlug: carousel.slug,
      containingPageSlug: pageSlug,
      artificialGuid: uuidv4(),
      guid: uuidv4(),
    };
  }

  // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
  public recommendationCarouselMapper(
    carousel: RecommendationCarousel,
    contentGroupData: Content[][],
    index: number,
    currentLang: string,
    pageURL: string,
    localizedTitle: LocalizedData<string>
  ): RecommendationCarouselViewModel {
    const formattedCarouseLocalizedlTitle = formatRecommendationLocalizedTitle(localizedTitle, carousel.titleLocalized);
    return {
      items:
        contentGroupData[index]?.map((contentPreview, idx) =>
          this.carouselItemMapper(contentPreview, idx, ContentViewModelCarouselType.Reccommendation)
        ) ?? [],
      titleToDisplay: formattedCarouseLocalizedlTitle[currentLang],
      titleLocalized: formattedCarouseLocalizedlTitle,
      layout: carousel.layout,
      slug: carousel.slug,
      routerLink: pageURL,
      type: CarouselType.RecommendationCarousel,
      recommendationTrayType: carousel.recommendationType,
      isTop10: carousel.isTop10,
    };
  }

  // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
  public marketingEmailBlockerMapper(
    marketingEmailBlockerModel: MarketingEmailBlocker,
    displayPriceAndCurrency: DisplayPriceAndCurrency,
    currentLang: string
  ): MarketingEmailBlockerViewModel {
    return {
      slug: marketingEmailBlockerModel.slug,
      title: marketingEmailBlockerModel.titleLocalized[currentLang],
      type: marketingEmailBlockerModel.type,
      buttonText: marketingEmailBlockerModel.buttonTextLocalized[currentLang],
      inputLabel: marketingEmailBlockerModel.inputLabelLocalized[currentLang],
      currency: displayPriceAndCurrency?.currency,
      subscriptionPrice: displayPriceAndCurrency?.subscriptionPrice,
      hintText: marketingEmailBlockerModel.hintTextLocalized?.[currentLang],
      sublineText: marketingEmailBlockerModel.sublineTextLocalized?.[currentLang],
      buttonLabelEnglish: marketingEmailBlockerModel.buttonTextLocalized.en,
    };
  }

  // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
  public spotlightMapper(spotlight: SpotlightTray, currentLang: string): SpotlightViewModel {
    return {
      type: 'SpotlightViewModel',
      title: spotlight.titleLocalized[currentLang],
      description: spotlight.description[currentLang],
      buttonText: spotlight.buttonText[currentLang],
      imageUrlsByPlatform: spotlight.backgroundImageUrlsByPlatform.map((backgroundImage) => ({
        platform: backgroundImage.platform,
        imageUrl: backgroundImage.imageUrl[currentLang],
      })),
      backgroundImageUrl: spotlight.backgroundImageUrl[currentLang],
      slug: spotlight.slug,
    };
  }
}
